import React, { useState, useEffect } from "react";
import SEO from "../../Components/SEO/SEO";
import SearchIcon from "@mui/icons-material/Search";
import CategoryIcon from "@mui/icons-material/Category";
import StarIcon from "@mui/icons-material/Star";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import "./Products.css";
import { IApp } from "../../Types/app.types";
import { appsAPI } from "../../admin/services/api";

const Products: React.FC = () => {
  const [apps, setApps] = useState<IApp[]>([]);
  const [featuredApps, setFeaturedApps] = useState<IApp[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  // const categories = ["all", "web", "mobile", "desktop", "utility", "business"];

  useEffect(() => {
    loadApps();
  }, []);

  const loadApps = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await appsAPI.getAll();

      const allApps = response.data.data || response.data || [];
      setApps(allApps);

      // Filter featured apps
      const featured = allApps.filter((app: IApp) => app.featured);
      setFeaturedApps(featured.slice(0, 3));
    } catch (error) {
      console.error("Error loading apps:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const filteredApps = apps.filter((app) => {
    const matchesSearch =
      app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || app.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getAvatarUrl = (name: string) => {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(
      name
    )}&size=400&background=FF9933&color=fff&bold=true&font-size=0.4`;
  };

  if (loading) {
    return (
      <div className="products-page">
        <SEO />
        <div className="products-loading-container">
          <div className="products-loading-content">
            <div className="loading-spinner-large"></div>
            <h2>Loading Products...</h2>
            <p>Please wait while we fetch our products</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="products-page">
      <SEO />

      {/* Hero Section */}
      <section className="products-hero">
        <div className="products-hero-particles"></div>
        <div className="container">
          <div className="products-hero-content">
            <span className="section-badge">Our Products</span>
            <h1>Discover Our Solutions</h1>
            <p>
              Explore our comprehensive range of applications and tools designed
              to empower your business
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="products-filters">
        <div className="container">
          <form onSubmit={handleSearch} className="search-box">
            <SearchIcon className="search-icon" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button type="submit" className="search-submit-btn">
              Search
            </button>
          </form>

          {/* <div className="category-filters">
            {categories.map((category) => (
              <button
                key={category}
                className={`category-btn ${
                  selectedCategory === category ? "active" : ""
                }`}
                onClick={() => setSelectedCategory(category)}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div> */}

          {(searchQuery || selectedCategory !== "all") && (
            <div className="active-filters">
              <span>Active filters:</span>
              {searchQuery && (
                <span className="filter-tag">
                  Search: "{searchQuery}"
                  <button onClick={() => setSearchQuery("")}>×</button>
                </span>
              )}
              {selectedCategory !== "all" && (
                <span className="filter-tag">
                  Category: {selectedCategory}
                  <button onClick={() => setSelectedCategory("all")}>×</button>
                </span>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Main Content */}
      <section className="products-content">
        <div className="container">
          <div className="products-layout">
            {/* Main Products Grid */}
            <div className="products-main">
              {loading ? (
                <div className="loading-grid">
                  <div className="spinner"></div>
                  <p>Loading products...</p>
                </div>
              ) : filteredApps.length === 0 ? (
                <div className="no-results">
                  <div className="no-results-icon">📦</div>
                  <h2>No Products Found</h2>
                  <p>
                    {searchQuery
                      ? `No products match "${searchQuery}". Try different keywords.`
                      : selectedCategory !== "all"
                      ? `No products available in the ${selectedCategory} category.`
                      : "No products available at the moment. Check back soon!"}
                  </p>
                  <button
                    className="btn-primary"
                    onClick={() => {
                      setSearchQuery("");
                      setSelectedCategory("all");
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              ) : (
                <>
                  <div className="results-info">
                    <p>
                      Showing {filteredApps.length} of {apps.length} products
                      {selectedCategory !== "all" && ` in ${selectedCategory}`}
                    </p>
                  </div>

                  <div className="products-grid">
                    {filteredApps.map((app) => (
                      <a
                        key={app._id}
                        href={app.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="product-card"
                      >
                        <div className="product-card-image">
                          <img
                            src={app.image || getAvatarUrl(app.name)}
                            alt={app.name}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = getAvatarUrl(app.name);
                            }}
                          />
                          <div className="product-card-overlay">
                            <span className="category-badge">
                              {app.category}
                            </span>
                            {app.featured && (
                              <span className="featured-star">⭐</span>
                            )}
                          </div>
                        </div>
                        <div className="product-card-content">
                          <h3 className="product-card-title">{app.name}</h3>
                          <p className="product-card-description">
                            {app.description}
                          </p>
                          <div className="product-card-footer">
                            <span className="view-link">
                              View Product <OpenInNewIcon />
                            </span>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Sidebar */}
            <aside className="products-sidebar">
              {/* Featured Products */}
              {featuredApps.length > 0 && (
                <div className="sidebar-widget featured-widget">
                  <div className="widget-header">
                    <StarIcon className="widget-icon" />
                    <h3>Featured Products</h3>
                  </div>
                  <div className="widget-content">
                    {featuredApps.map((app) => (
                      <a
                        key={app._id}
                        href={app.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="sidebar-product"
                      >
                        <div className="sidebar-product-image">
                          <img
                            src={app.image || getAvatarUrl(app.name)}
                            alt={app.name}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = getAvatarUrl(app.name);
                            }}
                          />
                        </div>
                        <div className="sidebar-product-content">
                          <h4>{app.name}</h4>
                          <div className="sidebar-product-category">
                            <CategoryIcon />
                            <span>{app.category}</span>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
